import { memo, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  Tooltip,
  IconButton,
} from '@mui/material'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import EditOutlined from '@mui/icons-material/EditOutlined'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'

import { useAppDispatch } from 'redux/Hook'
import { setSavedFilterName } from 'redux/slices/FilterBuilder'
import RenameFilterSet from 'components/Reporting/Overview/FilterBuilder/FilterSet/RenameFilter'
import DeleteFilterSet from 'components/Reporting/Overview/FilterBuilder/FilterSet/DeleteFilter'
import { isMobileView } from 'lib/GlobalConstants'
import { FilterSetStyles } from 'components/Reporting/Overview/FilterBuilder/ButtonGroup/StyleConstants'

const ButtonGroupMenu = ({
  options,
  open,
  anchorRef,
  handleClose,
  selectedIndex,
  handleMenuClick,
  handleRenameSelection,
  handleDeleteSelection,
  longText,
}: any) => {
  const [visibility, setVisibility] = useState(false)
  const [deleteModalVisibity, setDeleteModalVisisbilty] = useState(false)
  const [mouseMoved, setMouseMoved] = useState<any>({})
  const optionsLength = useMemo(() => options?.length, [options])
  const dispatch = useAppDispatch()
  const menuRef: any = useRef(null)
  const { t } = useTranslation()
  const renameRef: any = useRef(null)

  const onCloseModal = async (filter: string) => {
    await (filter === 'rename'
      ? setVisibility(false)
      : setDeleteModalVisisbilty(false))
    dispatch(setSavedFilterName({ originalFilterName: '' }))
  }
  const handleClick = async (
    filterName: string,
    filterId: string,
    filter: string,
  ) => {
    filter === 'rename' ? setVisibility(true) : setDeleteModalVisisbilty(true)
    renameRef?.current?.blur()
    await dispatch(
      setSavedFilterName({ originalFilterName: filterName, filterId }),
    )
  }

  const RenameIconButton = ({
    filterName,
    filterId,
  }: {
    filterName: string
    filterId: string
  }) => (
    <Tooltip title={t('common.rename')}>
      <IconButton
        size="small"
        aria-label="rename item"
        data-testid="rename-item"
        onClick={() => handleClick(filterName, filterId, 'rename')}
        ref={renameRef}
      >
        <EditOutlined color="textSecondary" sx={{ fontSize: '1rem' }} />
      </IconButton>
    </Tooltip>
  )

  const DeleteIconButton = ({
    filterName,
    filterId,
  }: {
    filterName: string
    filterId: string
  }) => (
    <Tooltip title={t('common.delete')}>
      <IconButton
        size="small"
        aria-label="delete item"
        data-testid="delete-item"
        onClick={() => handleClick(filterName, filterId, 'delete')}
        ref={renameRef}
      >
        <DeleteOutlined color="textSecondary" sx={{ fontSize: '1rem' }} />
      </IconButton>
    </Tooltip>
  )

  const onMouseEnter = (index: any) => {
    setMouseMoved({ [index]: true })
  }

  const onMouseLeave = (index: any) => {
    setMouseMoved({ [index]: false })
  }

  const renameProps = {
    testProps: false,
    handleRenameSelection,
    onCloseModal,
    visibility,
  }

  const deleteProps = {
    testProps: false,
    handleDeleteSelection,
    onCloseModal,
    deleteModalVisibity,
  }

  return (
    <>
      <RenameFilterSet {...renameProps} />
      <DeleteFilterSet {...deleteProps} />
      {optionsLength ? (
        <Popper
          sx={{
            zIndex: 2,
          }}
          open={open.isOpen}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          placement="bottom-start"
          ref={menuRef}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              {}
              <Paper
                sx={
                  anchorRef &&
                  FilterSetStyles({ isMobileView, anchorRef, optionsLength })?.[
                    'paper'
                  ]
                }
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu" autoFocusItem={false}>
                    {options.map(({ filterName, id }: any, index: number) => {
                      return (
                        <MenuItem
                          key={index}
                          selected={index === selectedIndex}
                          autoFocus={false}
                          onMouseEnter={() => onMouseEnter(index)}
                          onMouseLeave={() => onMouseLeave(index)}
                        >
                          <Box
                            width={'100%'}
                            display="flex"
                            justifyContent={'space-between'}
                            height={'26px'}
                          >
                            <Box
                              width={'100%'}
                              justifyContent={'flex-start'}
                              onClick={(event: any) => {
                                handleMenuClick(event, index)
                              }}
                            >
                              {longText({
                                filterName,
                                componentPropsSx: true,
                              })}
                            </Box>

                            {mouseMoved[index] && (
                              <Box justifyContent={'flex-end'} display={'flex'}>
                                <RenameIconButton
                                  filterName={filterName}
                                  filterId={id}
                                />
                                <DeleteIconButton
                                  filterName={filterName}
                                  filterId={id}
                                />
                              </Box>
                            )}
                          </Box>
                        </MenuItem>
                      )
                    })}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      ) : null}
    </>
  )
}

export default memo(ButtonGroupMenu)
